import { Link } from 'react-router-dom';
import type { PriceItem } from '@/types';
import { formatPercent, formatPrice, formatSigned } from '@/utils/format';
import Sparkline from './Sparkline';

interface PriceCardProps {
  item: PriceItem;
}

export default function PriceCard({ item }: PriceCardProps) {
  const up = item.change >= 0;
  const typeLabel = item.type === 'spot' ? '现货' : '期货';
  const digits = item.price < 100 ? 2 : 2;

  return (
    <Link
      to={`/detail/price/${item.id}`}
      className="group flex flex-col rounded-lg border border-background-200 bg-background-50 p-5 transition-all hover:border-primary-300 hover:bg-primary-50/40"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-base font-semibold text-foreground-950 group-hover:text-primary-700">
            {item.name}
          </h3>
          <p className="mt-0.5 text-xs text-foreground-500">
            {item.symbol} · {item.market}
          </p>
        </div>
        <span className="shrink-0 rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900">
          {typeLabel}
        </span>
      </div>

      <div className="mt-4 flex items-end justify-between gap-3">
        <div>
          <div className={`text-2xl font-bold tabular-nums ${up ? 'text-accent-600' : 'text-secondary-500'}`}>
            {formatPrice(item.price, digits)}
          </div>
          <div className="mt-1 text-xs text-foreground-500">{item.unit}</div>
        </div>
        <div
          className={`flex items-center gap-1 rounded-md px-2 py-1 text-sm font-semibold tabular-nums ${
            up ? 'bg-accent-100 text-accent-800' : 'bg-secondary-100 text-secondary-600'
          }`}
        >
          <i className={up ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} aria-hidden="true"></i>
          {formatPercent(item.changePercent)}
        </div>
      </div>

      <div className="mt-3">
        <Sparkline data={item.trend} positive={up} height={44} />
      </div>

      <div className="mt-3 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
        <span>涨跌 {formatSigned(item.change, digits)}</span>
        <span className="flex items-center gap-1">
          <i className="ri-time-line" aria-hidden="true"></i>
          {item.updatedAt.slice(5)}
        </span>
      </div>
    </Link>
  );
}